import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import {
  FiCheckSquare,
  FiPlus,
  FiAlertCircle,
  FiActivity,
  FiEdit,
} from 'react-icons/fi';
import dayjs from 'dayjs';

import { Input } from '../common/Input';
import { Patient } from '../../interfaces/patient';
import { NoDataPlaceholder } from '../common/NoDataPlaceholder';
import { Button } from '../common/Button';

interface PatientSelectionListProps {
  patients: Patient[];
  isLoading: boolean;
  isError: boolean;
  onPatientSelect: (patient: Patient) => void;
  onSearchChange: (searchTerm: string) => void;
  selectedPatient: Patient | null;
  isSearchDisabled?: boolean;
  onAddPregnancy?: (patient: Patient) => void;
  onEditPregnancy?: (patient: Patient) => void;
}

export function PatientSelectionList({
  patients,
  isLoading,
  isError,
  onPatientSelect,
  onSearchChange,
  selectedPatient,
  isSearchDisabled = false,
  onAddPregnancy,
  onEditPregnancy,
}: PatientSelectionListProps) {
  const { t } = useTranslation('RecentPatients');
  const debouncedSearchChange = useDebouncedCallback(onSearchChange, 500);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const calculateGA = (lmp?: string | null) => {
    if (!lmp) return null;
    const lmpDay = dayjs(lmp).startOf('day');
    const today = dayjs().startOf('day');
    const diffDays = today.diff(lmpDay, 'day');
    if (diffDays < 0) return null;
    return { weeks: Math.floor(diffDays / 7), days: diffDays % 7 };
  };

  const renderPregnancyCell = (patient: Patient) => {
    const pregnancy = (patient as any).pregnancy;

    if (!pregnancy) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-gray-400">
          No Pregnancy
        </span>
      );
    }

    const isActive = pregnancy.pregnancy_status === 'Active';
    const eddPassed = pregnancy.estimated_due_date
      ? dayjs().isAfter(dayjs(pregnancy.estimated_due_date), 'day')
      : false;
    const ga = calculateGA(pregnancy.last_menstrual_period);

    const showAsPassedDue = eddPassed && isActive;

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold ${
              showAsPassedDue
                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400'
                : isActive
                  ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-400'
                  : pregnancy.pregnancy_status === 'Delivered'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            {showAsPassedDue ? (
              <FiAlertCircle size={10} className="mr-1" />
            ) : (
              <FiActivity size={10} className="mr-1" />
            )}
            {showAsPassedDue ? 'Past Due' : pregnancy.pregnancy_status}
          </span>
        </div>
        {ga && !showAsPassedDue && (
          <span className="text-[10px] text-slate-600 dark:text-slate-400">
            GA: {ga.weeks}w {ga.days}d
          </span>
        )}
        {pregnancy.estimated_due_date && (
          <span className="text-[10px] text-slate-600 dark:text-slate-400">
            EDD: {dayjs(pregnancy.estimated_due_date).format('DD MMM YY')}
          </span>
        )}
      </div>
    );
  };

  const renderPregnancyAction = (patient: Patient) => {
    const pregnancy = (patient as any).pregnancy;
    const isActive = pregnancy?.pregnancy_status === 'Active';

    if (!pregnancy) {
      return (
        <Button
          variant="secondary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onAddPregnancy?.(patient);
          }}
        >
          <FiPlus size={12} className="mr-1" />
          Add
        </Button>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        <Button
          variant="secondary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onEditPregnancy?.(patient);
          }}
        >
          <FiEdit size={12} className="mr-1" />
          Edit
        </Button>
        {!isActive && onAddPregnancy && (
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onAddPregnancy(patient);
            }}
          >
            <FiPlus size={12} className="mr-1" />
            New
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="relative space-y-4 h-fit">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-gray-500" />
        <Input
          type="text"
          placeholder="Search patients by name or email..."
          className="pl-10"
          disabled={selectedPatient !== null || isSearchDisabled}
          onChange={(e) => debouncedSearchChange(e.target.value)}
        />
      </div>

      <div className="border border-slate-200 dark:border-gray-700 rounded-lg bg-card-color-100 dark:bg-gray-800 overflow-hidden min-h-[350px] flex flex-col">
        {isLoading && (
          <div className="flex-grow flex items-center justify-center">
            <p className="text-title-page-color-300 dark:text-gray-400">
              Loading...
            </p>
          </div>
        )}
        {isError && (
          <div className="flex-grow flex items-center justify-center p-4 text-center text-red-500">
            Failed to load patients.
          </div>
        )}
        {!isLoading && !isError && patients.length === 0 && (
          <NoDataPlaceholder
            className="flex-grow"
            message={t('noPatientsFound')}
          />
        )}

        {!isLoading && patients.length > 0 && (
          <div className="flex-grow overflow-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-gray-700 hidden md:table">
              <thead className="bg-card-color-200 dark:bg-gray-900">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-title-page-color-300 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-title-page-color-300 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-title-page-color-300 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Date of Birth
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-title-page-color-300 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Pregnancy
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-title-page-color-300 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card-color-100 dark:bg-gray-800 divide-y divide-slate-200 dark:divide-gray-700">
                {patients.map((patient) => (
                  <tr
                    key={patient.id}
                    onClick={() => onPatientSelect(patient)}
                    className={`${isSearchDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} transition-colors ${
                      selectedPatient?.id === patient.id
                        ? 'bg-blue-100 dark:bg-blue-900'
                        : 'hover:bg-card-color-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-slate-200 dark:bg-gray-600 rounded-full text-slate-600 dark:text-gray-300 font-semibold uppercase">
                          {patient.patient_name.charAt(0)}
                          {patient.patient_last_name.charAt(0)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-title-page-color-100 dark:text-white">
                            {patient.patient_name} {patient.patient_last_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-title-page-color-300 dark:text-gray-300">
                      {patient.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-title-page-color-300 dark:text-gray-300">
                      {formatDate(patient.birth_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderPregnancyCell(patient)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div onClick={(e) => e.stopPropagation()}>
                        {renderPregnancyAction(patient)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="divide-y divide-slate-200 dark:divide-gray-700 md:hidden">
              {patients.map((patient) => (
                <div
                  key={patient.id}
                  onClick={() => onPatientSelect(patient)}
                  className={`${isSearchDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} p-4 transition-colors duration-150 ${selectedPatient?.id === patient.id ? 'bg-blue-50 dark:bg-blue-900/50' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-200 dark:bg-gray-600 rounded-full text-gray-600 dark:text-gray-300 font-semibold uppercase">
                        {patient.patient_name.charAt(0)}
                        {patient.patient_last_name.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {patient.patient_name} {patient.patient_last_name}
                        </div>
                      </div>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      {renderPregnancyAction(patient)}
                    </div>
                  </div>
                  <div className="mt-3 space-y-2 pl-14">
                    <div className="flex items-baseline text-xs">
                      <span className="font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                        Email:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 truncate">
                        {patient.email || 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-baseline text-xs">
                      <span className="font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                        DOB:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {formatDate(patient.birth_date)}
                      </span>
                    </div>
                    <div className="flex items-baseline text-xs">
                      <span className="font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                        Pregnancy:
                      </span>
                      <div className="text-gray-700 dark:text-gray-300">
                        {renderPregnancyCell(patient)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
